import { apiFetch, readApiError } from "../api/api-client";
import type { ProjectDetail, ProjectSceneState, ProjectSummary } from "./project-types";

export async function listProjects() {
  return requestJson<ProjectSummary[]>("/api/projects", undefined, "读取项目列表失败");
}

export async function createProject(name: string) {
  return requestJson<ProjectSummary>(
    "/api/projects",
    { body: JSON.stringify({ name }), headers: jsonHeaders, method: "POST" },
    "创建项目失败"
  );
}

export async function getProject(id: string) {
  return requestJson<ProjectDetail>(
    `/api/projects/${encodeURIComponent(id)}`,
    undefined,
    "读取项目失败"
  );
}

export async function renameProject(id: string, name: string, expectedVersion: number) {
  return requestJson<ProjectSummary>(
    `/api/projects/${encodeURIComponent(id)}`,
    {
      body: JSON.stringify({ expectedVersion, name }),
      headers: jsonHeaders,
      method: "PATCH"
    },
    "修改项目名称失败"
  );
}

export async function deleteProject(id: string, expectedVersion: number) {
  await requestJson(
    `/api/projects/${encodeURIComponent(id)}`,
    { body: JSON.stringify({ expectedVersion }), headers: jsonHeaders, method: "DELETE" },
    "删除项目失败"
  );
}

export async function saveProjectSceneState(
  projectId: string,
  drawingGeneration: number,
  expectedVersion: number,
  state: ProjectSceneState
) {
  return requestJson<{ updatedAt: string; version: number }>(
    `/api/projects/${encodeURIComponent(projectId)}/state`,
    {
      body: JSON.stringify({ drawingGeneration, expectedVersion, state }),
      headers: jsonHeaders,
      method: "PUT"
    },
    "保存场景设置失败"
  );
}

export async function loadProjectDrawing(project: ProjectDetail) {
  if (!project.drawingFileName) {
    return undefined;
  }
  const response = await apiFetch(`/api/projects/${encodeURIComponent(project.id)}/drawing`);
  if (!response.ok) {
    throw new Error(await readApiError(response, "读取项目图纸失败"));
  }
  const compressed = await response.blob();
  const stream = compressed.stream().pipeThrough(new DecompressionStream("gzip"));
  return new File([await new Response(stream).blob()], project.drawingFileName, {
    type: "application/dxf"
  });
}

export async function replaceProjectDrawing(project: ProjectDetail, file: File) {
  const idempotencyKey = crypto.randomUUID();
  const operation = await requestJson<{ maxCompressedBytes: number; operationId: string }>(
    `/api/projects/${encodeURIComponent(project.id)}/drawing-uploads`,
    {
      body: JSON.stringify({
        expectedProjectVersion: project.version,
        idempotencyKey
      }),
      headers: jsonHeaders,
      method: "POST"
    },
    "创建图纸上传任务失败"
  );
  const [compressed, sha256] = await Promise.all([gzipFile(file), sha256File(file)]);
  if (compressed.size > operation.maxCompressedBytes) {
    throw new Error("压缩后的图纸不能超过 80 MB");
  }

  const operationPath = `/api/projects/${encodeURIComponent(project.id)}/drawing-uploads/${encodeURIComponent(operation.operationId)}`;
  const uploadResponse = await apiFetch(operationPath, {
    body: compressed,
    headers: { "Content-Type": "application/gzip" },
    method: "PUT"
  });
  if (!uploadResponse.ok) {
    throw new Error(await readApiError(uploadResponse, "上传图纸失败"));
  }
  return requestJson<ProjectDetail>(
    `${operationPath}/commit`,
    {
      body: JSON.stringify({
        expectedProjectVersion: project.version,
        fileName: file.name,
        originalBytes: file.size,
        sha256
      }),
      headers: jsonHeaders,
      method: "POST"
    },
    "启用新图纸失败"
  );
}

export async function removeProjectDrawing(project: ProjectDetail) {
  return requestJson<{ drawingGeneration: number; updatedAt: string; version: number }>(
    `/api/projects/${encodeURIComponent(project.id)}/drawing`,
    {
      body: JSON.stringify({ expectedVersion: project.version }),
      headers: jsonHeaders,
      method: "DELETE"
    },
    "删除项目图纸失败"
  );
}

const jsonHeaders = { "Content-Type": "application/json" };

async function requestJson<T = unknown>(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  fallback: string
) {
  const response = await apiFetch(input, init);
  if (!response.ok) {
    throw new Error(await readApiError(response, fallback));
  }
  return (await response.json()) as T;
}

async function gzipFile(file: File) {
  const stream = file.stream().pipeThrough(new CompressionStream("gzip"));
  return new Response(stream).blob();
}

async function sha256File(file: File) {
  const hash = new Uint8Array(await crypto.subtle.digest("SHA-256", await file.arrayBuffer()));
  return Array.from(hash, (byte) => byte.toString(16).padStart(2, "0")).join("");
}
